import { useState } from 'react';
import { ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import type { AnomalyRow, ResolveAction, Severity } from '../../hooks/useAnomalies';
import { groupAnomalies, type AnomalyGroup } from './groupAnomalies';

interface Props {
  rows: AnomalyRow[];
  onResolve: (keys: AnomalyRow[], action: ResolveAction) => void;
  onRepull: (group: AnomalyGroup) => void;
  onRescan: (group: AnomalyGroup) => void;
  repullPendingKeys?: Set<string>;
  rescanPendingKeys?: Set<string>;
}

export function AnomaliesTable({
  rows,
  onResolve,
  onRepull,
  onRescan,
  repullPendingKeys,
  rescanPendingKeys,
}: Props) {
  const groups = groupAnomalies(rows);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());

  const selectedGroupObjs = groups.filter((g) => selectedGroups.has(g.key));
  const allSelectedRows: AnomalyRow[] = selectedGroupObjs.flatMap((g) => g.rows);

  function toggleExpanded(key: string) {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  }

  function toggleSelected(key: string) {
    const next = new Set(selectedGroups);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedGroups(next);
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {selectedGroupObjs.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-950 px-4 py-2 flex gap-2 items-center text-xs">
          <span>
            {selectedGroupObjs.length} group{selectedGroupObjs.length === 1 ? '' : 's'} selected (
            {allSelectedRows.length} anomalies)
          </span>
          <button
            onClick={() => selectedGroupObjs.forEach((g) => onRepull(g))}
            className="px-2 py-1 rounded bg-purple-600 text-white"
            title="Re-pull data from upstream for the selected platform-days"
          >
            Re-pull all
          </button>
          <button
            onClick={() => onResolve(allSelectedRows, 'acknowledge')}
            className="px-2 py-1 rounded bg-blue-600 text-white"
          >
            Acknowledge all
          </button>
          <button
            onClick={() => onResolve(allSelectedRows, 'ignore')}
            className="px-2 py-1 rounded bg-gray-600 text-white"
          >
            Ignore all
          </button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500">
            <th className="px-2 py-2 w-8"></th>
            <th className="px-2 py-2 w-8"></th>
            <th className="text-left px-3 py-2 font-medium">Severity</th>
            <th className="text-left px-3 py-2 font-medium">Date</th>
            <th className="text-left px-3 py-2 font-medium">Store</th>
            <th className="text-left px-3 py-2 font-medium">Platform</th>
            <th className="text-right px-3 py-2 font-medium">Anomalies</th>
            <th className="text-left px-3 py-2 font-medium">Detected</th>
            <th className="text-left px-3 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isExpanded = expanded.has(g.key);
            const isSelected = selectedGroups.has(g.key);
            const repulling = repullPendingKeys?.has(g.key) ?? false;
            const rescanning = rescanPendingKeys?.has(g.key) ?? false;
            return (
              <GroupRows
                key={g.key}
                group={g}
                expanded={isExpanded}
                selected={isSelected}
                onToggleExpanded={() => toggleExpanded(g.key)}
                onToggleSelected={() => toggleSelected(g.key)}
                onResolve={onResolve}
                onRepull={() => onRepull(g)}
                onRescan={() => onRescan(g)}
                repulling={repulling}
                rescanning={rescanning}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface GroupRowsProps {
  group: AnomalyGroup;
  expanded: boolean;
  selected: boolean;
  onToggleExpanded: () => void;
  onToggleSelected: () => void;
  onResolve: (keys: AnomalyRow[], action: ResolveAction) => void;
  onRepull: () => void;
  onRescan: () => void;
  repulling: boolean;
  rescanning: boolean;
}

function GroupRows({
  group,
  expanded,
  selected,
  onToggleExpanded,
  onToggleSelected,
  onResolve,
  onRepull,
  onRescan,
  repulling,
  rescanning,
}: GroupRowsProps) {
  return (
    <>
      <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/40 dark:bg-gray-950/40">
        <td className="px-2 py-2">
          <input type="checkbox" checked={selected} onChange={onToggleSelected} />
        </td>
        <td className="px-2 py-2">
          <button onClick={onToggleExpanded} className="text-gray-500 hover:text-gray-900">
            {expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </button>
        </td>
        <td className="px-3 py-2">
          <SeverityBadge s={group.worstSeverity} />
        </td>
        <td className="px-3 py-2 font-medium">{group.date}</td>
        <td className="px-3 py-2">
          {group.storeName} <span className="text-gray-400">#{group.storeId}</span>
        </td>
        <td className="px-3 py-2">{group.platform}</td>
        <td className="px-3 py-2 text-right font-mono text-xs">{group.rows.length}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{formatRelative(group.detectedAt)}</td>
        <td className="px-3 py-2 flex gap-1 flex-wrap">
          <button
            onClick={onRepull}
            disabled={repulling}
            title="Re-pull this platform's data for this day from upstream API"
            className="text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 disabled:opacity-50 inline-flex items-center gap-1"
          >
            <Download className="w-3 h-3" />
            {repulling ? 'Pulling…' : 'Re-pull'}
          </button>
          <button
            onClick={onRescan}
            disabled={rescanning}
            title="Re-run anomaly detection for this day"
            className="text-[11px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 disabled:opacity-50 inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            {rescanning ? 'Scanning…' : 'Rescan'}
          </button>
          <button
            onClick={() => onResolve(group.rows, 'acknowledge')}
            className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-700"
          >
            Ack all
          </button>
          <button
            onClick={() => onResolve(group.rows, 'ignore')}
            className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700"
          >
            Ignore all
          </button>
        </td>
      </tr>
      {expanded &&
        group.rows.map((r) => (
          <tr
            key={`${r.storeId}|${r.platform}|${r.metric}|${r.date}`}
            className="border-t border-gray-100 dark:border-gray-800"
          >
            <td className="px-2 py-2"></td>
            <td className="px-2 py-2"></td>
            <td className="px-3 py-2">
              <SeverityBadge s={r.severity} />
            </td>
            <td className="px-3 py-2 text-xs text-gray-500">↳ {r.metric}</td>
            <td className="px-3 py-2 text-xs text-gray-500" colSpan={2}>
              actual {formatValue(r.metric, r.actualValue)} · expected{' '}
              {formatValue(r.metric, r.expectedValue)} ·{' '}
              {formatDeviation(r.rule, r.deviation, r.streakLength)}
            </td>
            <td className="px-3 py-2 text-xs text-gray-400">{ruleLabel(r.rule)}</td>
            <td className="px-3 py-2"></td>
            <td className="px-3 py-2 flex gap-1">
              {r.status === 'open' && (
                <>
                  <button
                    onClick={() => onResolve([r], 'acknowledge')}
                    className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-700"
                  >
                    Ack
                  </button>
                  <button
                    onClick={() => onResolve([r], 'ignore')}
                    className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700"
                  >
                    Ignore
                  </button>
                </>
              )}
              {r.status !== 'open' && (
                <button
                  onClick={() => onResolve([r], 'reopen')}
                  className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700"
                >
                  Reopen
                </button>
              )}
            </td>
          </tr>
        ))}
    </>
  );
}

function SeverityBadge({ s }: { s: Severity }) {
  const cls =
    s === 'critical'
      ? 'bg-red-100 text-red-700'
      : s === 'warning'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-blue-100 text-blue-700';
  return (
    <span className={`text-[10px] uppercase font-medium px-2 py-0.5 rounded ${cls}`}>{s}</span>
  );
}

function formatValue(metric: string, value: number): string {
  if (metric === 'revenue' || metric === 'spend') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatDeviation(rule: string, deviation: number, streakLength: number): string {
  if (rule === 'zero_streak') return `down ${streakLength} ${streakLength === 1 ? 'day' : 'days'}`;
  if (rule === 'mad_zscore') return `z=${deviation.toFixed(2)}`;
  const pct = Math.round(deviation * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
}

function ruleLabel(rule: string): string {
  if (rule === 'zero_streak') return 'integration down';
  return rule;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}
